import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Inject,
  Injectable,
  inject,
  Renderer2,
  RendererFactory2,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface GtmWindow extends Window {
  dataLayer: Record<string, unknown>[];
}

export interface UserIdentifiedEvent {
  customer_id: number;
  customer_name: string;
  user_id: string;
  session_id: string;
  customer_domain?: string;
  is_login_after_onboarding?: boolean; // EDUS-2866 Event 5
}

/**
 * Richer identity payload destined for Userlane only.
 *
 * This is pushed as a separate `userlane_identify` dataLayer event — NOT folded
 * into `user_identified` — because `user_identified` feeds GA4, where raw email
 * and name are PII that Google's policy prohibits (see EDUS-2479's exclusion
 * test and EDUS-2571, which sends only the email domain to GA4).
 *
 * In GTM these fields must be mapped to the Userlane identify tag ONLY and must
 * never be forwarded to a GA4 tag. See docs/frontend/userlane-gtm-attributes.md.
 *
 * Only `user_id` and `customer_id` are guaranteed; every other field is optional
 * and MUST be omitted by the caller when absent (never sent as '' / null / []),
 * because Userlane rejects an attribute that carries no value (HTTP 422).
 */
export interface UserlaneIdentifyEvent {
  user_id: string;
  customer_id: number;
  customer_name?: string;
  session_id?: string;
  customer_domain?: string;
  user_email?: string;
  user_name?: string;
  db_user_id?: number;
  google_customer_id?: string;
  cdw_customer_id?: string;
  features?: string[];
  permission_codes?: string[];
}

/**
 * Action categories for the generic user_action event.
 * Extend this union type when adding new feature tracking.
 */
export type ActionCategory =
  | 'bulk_actions'
  | 'reports'
  | 'in_cell_editing'
  | 'filtering'
  | 'refresh_cache'
  | 'device_summary_tabs'
  | 'onboarding';

/**
 * Bulk action types for type-safe action tracking.
 */
export type BulkActionType =
  | 'move-ou'
  | 'disable'
  | 'enable'
  | 'deprovision'
  | 'profile-wipe'
  | 'factory-reset'
  | 'edit-import'
  | 'unknown';

/**
 * Generic user action event for tracking all user interactions in GA4.
 *
 * This is the base event structure pushed to dataLayer. Feature-specific
 * helper methods use this internally to maintain type safety while using
 * a single GTM event configuration.
 *
 * GTM team configures once:
 * - Custom Event trigger for "user_action"
 * - Variables for category, action, and metadata.*
 * - One GA4 tag with category/action as dimensions
 *
 * Future feature tracking requires only:
 * - New TypeScript interface for metadata
 * - New helper method in this service
 * - NO GTM configuration changes needed
 */
export interface UserActionEvent {
  /** High-level category (bulk_actions, reports, exports, filters) */
  category: ActionCategory;
  /** Specific action within the category (e.g., 'disable', 'run_report') */
  action: string;
  /** Feature-specific metadata (flexible, typed via helper methods) */
  metadata: Record<string, unknown>;
}

/**
 * Base metadata fields common to all bulk action tracking.
 */
export interface BulkActionBaseMetadata {
  /** Number of devices affected by this action */
  device_count: number;
  /** Fields updated in edit-import actions (e.g., ['notes', 'annotatedAssetId']) */
  fields_updated?: string[];
}

/**
 * Analytics metadata emitted by bulk action components for GTM tracking.
 *
 * Used in event emissions from cdw-bulk-update-action component to parent.
 * Parent component uses this to call GTM tracking methods.
 */
export interface BulkActionAnalyticsMetadata extends BulkActionBaseMetadata {
  /** User-facing action type (e.g., 'disable', 'enable', 'move-ou') */
  action_type: BulkActionType;
}

/**
 * In-cell editing metadata for tracking grid cell edits in GA4.
 *
 * Used by pushInCellEditAction() helper method. Surfaces which fields
 * users edit and at what scale.
 */
export interface InCellEditMetadata {
  /** Field/column being edited */
  field_name: string;
  /** Number of cells edited in batch (scale metric) */
  cell_count?: number;
}

/**
 * Filtering metadata for tracking which filters users apply in GA4.
 *
 * Surfaces filter adoption and usage patterns.
 */
export interface FilterActionMetadata {
  /** Array of filter names that were applied */
  filters_applied: string[];
}

/**
 * Refresh cache metadata for tracking cache refresh operations in GA4.
 *
 * Surfaces whether users refresh all devices or just selected ones.
 */
export interface RefreshCacheMetadata {
  /** Type of refresh operation */
  refresh_type: 'all' | 'selected';
  /** Number of devices refreshed (for 'selected' type) */
  device_count?: number;
}

/**
 * Device summary tab metadata for tracking tab switches in GA4.
 *
 * Surfaces which tabs users view within device summary (URL doesn't change,
 * so GA4's automatic page_view event won't catch it).
 */
export interface DeviceSummaryTabMetadata {
  /** Name of the tab being viewed */
  tab_name: string;
  /** Zero-based index of the tab */
  tab_index: number;
}

/**
 * Metadata field schemas by category.
 * Defines which fields belong to each event category for automatic clearing.
 * Adding a new event category only requires adding one schema entry here.
 */
const METADATA_SCHEMAS: Record<ActionCategory, string[]> = {
  bulk_actions: ['device_count', 'fields_updated'],
  reports: ['report_type'],
  in_cell_editing: ['field_name', 'cell_count'],
  filtering: ['filters_applied'],
  refresh_cache: ['refresh_type', 'device_count'],
  device_summary_tabs: ['tab_name', 'tab_index'],
  onboarding: ['outcome'],
};

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private renderer: Renderer2;
  private gtmWindow: GtmWindow;
  private initialized = false;
  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
    private router: Router
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.gtmWindow = window as unknown as GtmWindow;
  }

  public init(): void {
    const { analyticsEnv, gtmContainerId } = environment;

    if (this.initialized || this.isLocalEnv(analyticsEnv)) {
      return;
    }
    this.initialized = true;

    this.gtmWindow.dataLayer = this.gtmWindow.dataLayer || [];
    this.gtmWindow.dataLayer.push({ gtm_env: analyticsEnv });

    this.injectGtmScript(gtmContainerId);
    this.subscribeToNavigationEvents();
  }

  public pushUserIdentified(event: UserIdentifiedEvent): void {
    this.push({ event: 'user_identified', ...event });
  }

  /**
   * Push the Userlane-only identify payload. Kept separate from
   * {@link pushUserIdentified} so PII never reaches the GA4-fed
   * `user_identified` event. See docs/frontend/userlane-gtm-attributes.md.
   */
  public pushUserlaneIdentify(event: UserlaneIdentifyEvent): void {
    this.push({ event: 'userlane_identify', ...event });
  }

  /**
   * Generic user action event push. All user interaction tracking goes through
   * this method. Use feature-specific helper methods (pushBulkAction, etc.)
   * instead of calling this directly for type safety.
   *
   * Automatically clears all metadata fields to prevent GTM dataLayer persistence
   * issues where old values from previous events contaminate new events.
   */
  public pushUserAction(event: UserActionEvent): void {
    // Collect all possible metadata fields from all schemas
    const allFields = Object.values(METADATA_SCHEMAS).flat();

    // Clear all fields, then overlay actual metadata for this event
    const clearedMetadata: Record<string, unknown> = Object.fromEntries(
      allFields.map((field) => [field, undefined])
    );
    Object.assign(clearedMetadata, event.metadata);

    // Remove undefined values so they don't appear in GTM
    Object.keys(clearedMetadata).forEach((key) => {
      if (clearedMetadata[key] === undefined) {
        delete clearedMetadata[key];
      }
    });

    this.push({
      event: 'user_action',
      category: event.category,
      action: event.action,
      metadata: clearedMetadata,
    });
  }

  /**
   * Track in-cell editing operations. Fires when users edit grid cells,
   * save changes, or cancel edits.
   *
   * Example:
   * ```typescript
   * gtmService.pushInCellEditAction('cell_edited', {
   *   field_name: 'notes',
   *   grid_context: 'device_search',
   *   cell_count: 1,
   *   edit_type: 'single_cell',
   *   saved: true
   * });
   * ```
   */
  public pushInCellEditAction(
    action: string,
    metadata: InCellEditMetadata
  ): void {
    this.pushUserAction({
      category: 'in_cell_editing',
      action,
      metadata: { ...metadata },
    });
  }

  /**
   * Track the first time an admin engages with the onboarding wizard (EDUS-2866 Event 1).
   * Fires once per admin ever (localStorage dedup keyed by email). Called from
   * onWelcomeComplete() immediately after terms acceptance, or from loadProgress()
   * when a returning admin whose terms are already recorded enters the wizard.
   */
  public pushOnboardingAttempt(): void {
    this.pushUserAction({
      category: 'onboarding',
      action: 'attempt',
      metadata: {},
    });
  }

  /**
   * Track when user completes the onboarding wizard (EDUS-2866 Event 2).
   * Fires after successful wizard submission.
   *
   * Note: customer_domain is automatically included via user_properties
   * set by the GA4 Google Tag (no need to pass in metadata).
   */
  public pushOnboardingComplete(): void {
    this.pushUserAction({
      category: 'onboarding',
      action: 'complete',
      metadata: {},
    });
  }

  /**
   * Track when user sends team invitations during onboarding (EDUS-2866 Event 4).
   * Fires immediately when invites are successfully sent.
   *
   * Note: customer_domain is automatically included via user_properties
   * set by the GA4 Google Tag (no need to pass in metadata).
   */
  public pushInvitationsSent(): void {
    this.pushUserAction({
      category: 'onboarding',
      action: 'invitations_sent',
      metadata: {},
    });
  }

  public pushInitialSyncComplete(): void {
    this.pushUserAction({
      category: 'onboarding',
      action: 'sync',
      metadata: { outcome: 'succeeded' },
    });
  }

  public pushInitialSyncFailed(): void {
    this.pushUserAction({
      category: 'onboarding',
      action: 'sync',
      metadata: { outcome: 'failed' },
    });
  }

  private isLocalEnv(analyticsEnv: string): boolean {
    return analyticsEnv === 'local' || analyticsEnv.endsWith('-local');
  }

  private injectGtmScript(containerId: string): void {
    const script: HTMLScriptElement = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`;
    this.renderer.appendChild(this.document.head, script);
  }

  private subscribeToNavigationEvents(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.push({
          event: 'page_view',
          page_path: event.urlAfterRedirects,
          page_title: this.document.title,
        });
      });
  }

  private push(data: Record<string, unknown>): void {
    this.gtmWindow.dataLayer = this.gtmWindow.dataLayer || [];
    this.gtmWindow.dataLayer.push(data);
  }
}
